import { api } from './api';

// ---------------------------------------------------------------------------
// Types — GET /api/features (see apps/api/src/settings/features.controller.ts)
//
// Least-privilege carrier for client-visible feature flags: readable by ANY
// authenticated user, unlike GET /api/system-settings which requires the
// Admin-only `system_settings:read` permission.
// ---------------------------------------------------------------------------

/**
 * Client-facing picture-enhancement configuration. `enabled` already folds in
 * the PICTURE_ENHANCEMENT_ENABLED env kill-switch server-side, so the UI can
 * gate on it directly without consulting the feature record.
 */
export interface PictureEnhancementPolicy {
  /** `features.pictureEnhancement` AND the env kill-switch, resolved server-side. */
  enabled: boolean;
  /** When false, only "keep both" is offered on apply. */
  allowReplace: boolean;
  /** When true, "replace" is blocked if the enhanced image is smaller. */
  blockReplaceOnDownscale: boolean;
  /** Configured enhancement model NAME (never a credential); null when unset. */
  model: string | null;
  /**
   * Max photos one bulk-enhance request may queue (epic #420). Surfaced here
   * rather than only on the Admin-only system-settings read so a non-admin
   * collaborator's client can cap and label its selection up front instead of
   * discovering the limit as a 400.
   */
  maxBatchSize: number;
}

export interface FeatureFlags {
  /** Raw global feature-flag record, e.g. `{ pictureEnhancement: true }`. */
  features: Record<string, boolean>;
  pictureEnhancement: PictureEnhancementPolicy;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** Fetch client-visible feature flags for the current user. */
export async function getFeatures(): Promise<FeatureFlags> {
  return api.get<FeatureFlags>('/features');
}
